import OpButton from 'components/Common/op-ui-wrappers/OpButton/OpButton';
import OpIcon from 'components/Common/op-ui-wrappers/OpIcon/OpIcon';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import CheckBoxOptions from 'components/StudioSearchPage/StudioSearchResults/ItemsFilter/CheckBoxOptions/CheckBoxOptions';
import React, { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AttributeFilterOptionTemplate, FilterAttributeGroup } from 'store/category/types';
import fetchStudioSearchResultsOperation from 'store/studioSearch/operation/fetchStudioSearchResultsOperation';
import getNumberOfAppliedFilters from 'store/studioSearch/selector/getNumberOfAppliedFilters';
import getStudioSearchFilters from 'store/studioSearch/selector/getStudioSearchFilters';
import { resetStudioSearchFiltersAction, setStudioSearchRefetchFlagAction } from 'store/studioSearch/slice';
import isDesktop from 'store/app/selector/isDesktop';
import OpDrawer from 'components/Common/op-ui-wrappers/OpDrawer/OpDrawer';
import styles from './DropdownFilters.module.scss';

const DropdownFilters: FC = () => {
  const dispatch = useDispatch();
  const filtersContainerRef = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { t } = useTranslation();
  const isDesktopDevice = useSelector(isDesktop);

  const searchFilters = useSelector(getStudioSearchFilters);
  const numberOfAppliedFilters = useSelector(getNumberOfAppliedFilters);

  const buttonTitleAppliedFilters = numberOfAppliedFilters ? `(${numberOfAppliedFilters})` : '';

  const clearFilters = () => {
    dispatch(resetStudioSearchFiltersAction());
    dispatch(setStudioSearchRefetchFlagAction(true));
    dispatch(fetchStudioSearchResultsOperation(1));
  };

  const allFilters = [...(searchFilters?.attributeFilters || []), ...(searchFilters?.tagFilters || [])];

  const applyFilters = () => {
    dispatch(setStudioSearchRefetchFlagAction(true));
    dispatch(fetchStudioSearchResultsOperation(1));
  };

  const toggle = () => setFiltersOpen(!filtersOpen);

  return (
    <div className={styles.filterBtn}>
      <OpButton ref={filterButtonRef} size="medium" variant="secondary" onClick={toggle} icon-left="filter">
        <span className={styles.capitalize}>{`${t('txt_filters')} ${buttonTitleAppliedFilters}`}</span>
      </OpButton>

      {filtersOpen && isDesktopDevice && (
        <div className={styles.filterContainer} ref={filtersContainerRef}>
          <OpIcon icon="close" size="small" className={styles.close} onClick={() => setFiltersOpen(false)} />

          {allFilters.map(
            (attribute, index) =>
              attribute.optionTemplate === AttributeFilterOptionTemplate.CheckBoxOption && (
                <div className={styles.filterGroup}>
                  <OpText variant="body-large-bold" className={styles.filterName}>
                    {attribute.name}
                  </OpText>
                  <div className={styles.sizesContainer}>
                    <CheckBoxOptions
                      key={`pcbot${index}`}
                      attribute={attribute}
                      group={FilterAttributeGroup.tagFilters}
                      applyFilterCallback={applyFilters}
                    />
                  </div>
                </div>
              ),
          )}

          <div className={styles.actionButtons}>
            <OpButton
              block
              variant="secondary"
              size="medium"
              className={styles.clearFiltersBtn}
              onClick={() => clearFilters()}
            >
              <span className={styles.capitalize}>{t('txt_clear_filters')}</span>
            </OpButton>
          </div>
        </div>
      )}

      {!isDesktopDevice && (
        <OpDrawer visible={filtersOpen} onClose={() => setFiltersOpen(false)}>
          <div slot="label" className={styles.drawerLabel}>
            <OpText className={styles.capitalize} variant="title-medium-bold">
              {t('txt_filters')}
            </OpText>
          </div>

          <div slot="content" className={styles.content}>
            {allFilters.map(
              (attribute, index) =>
                attribute.optionTemplate === AttributeFilterOptionTemplate.CheckBoxOption && (
                  <>
                    <div className={styles.filterGroup}>
                      <OpText variant="body-large-regular">{attribute.name}</OpText>
                      <CheckBoxOptions
                        key={`pcbot${index}`}
                        attribute={attribute}
                        group={FilterAttributeGroup.tagFilters}
                        applyFilterCallback={applyFilters}
                      />
                    </div>
                  </>
                ),
            )}
          </div>

          <div slot="actions" className={styles.drawerActions}>
            <OpButton
              block
              variant="secondary"
              size="large"
              className={styles.clearFiltersBtn}
              onClick={() => clearFilters()}
            >
              <span className={styles.capitalize}>{t('txt_clear_filters')}</span>
            </OpButton>
          </div>
        </OpDrawer>
      )}
    </div>
  );
};

export default DropdownFilters;
